/*
 Copyright (c) 2019 - Indigen Solutions
 * Authors:
 *   - Bichoy Messiha <bm@indigen.com>
 * NOTICE: All information contained herein is, and remains
 * the property of Indigen Solutions and its suppliers,
 * if any.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Indigen Solutions.
 */

import Axios from "axios";
import { isNil } from "lodash";
import { getCookie } from "../utils/cookies";
import { AuthApi } from "./AuthApi";
import { CaApi } from "./CaApi";
import { InsurerApi } from "./InsurerApi";
import { ConventionApi } from "./ConventionApi";
import { LinkedContentApi } from "./LinkedContentApi";
import { UserApi } from "./UserApi";
import { MagApi } from "./MagApi";
import { PushApi } from "./PushApi";
import { ContactApi } from "./ContactApi";
import { AnnotationOptionApi } from "./AnnotationOptionApi";
import { SubjectApi } from "./SubjectApi";

export class ApiService {
    constructor(config) {
        this._accessToken = null;
        this._authApi = null;
        this._caApi = null;
        this._linkedContentApi = null;
        this._conventionApi = null;
        this._insurerApi = null;
        this._userApi = null;
        this._magApi = null;
        this._pushApi = null;
        this._contactApi = null;
        this.apiUrl = config.apiUrl;
        if (!this._accessToken) {
            this.setAccessToken(getCookie("jwtToken"));
        }
    }

    get push() {
        if (isNil(this._pushApi)) {
            this._pushApi = new PushApi(this);
        }

        return this._pushApi;
    }

    get auth() {
        if (isNil(this._authApi)) {
            this._authApi = new AuthApi(this);
        }

        return this._authApi;
    }

    get ca() {
        if (isNil(this._caApi)) {
            this._caApi = new CaApi(this);
        }

        return this._caApi;
    }

    get convention() {
        if (isNil(this._conventionApi)) {
            this._conventionApi = new ConventionApi(this);
        }

        return this._conventionApi;
    }

    get linkedContent() {
        if (isNil(this._linkedContentApi)) {
            this._linkedContentApi = new LinkedContentApi(this);
        }

        return this._linkedContentApi;
    }
    
    get insurer() {
        if (isNil(this._insurerApi)) {
            this._insurerApi = new InsurerApi(this);
        }

        return this._insurerApi;
    }
   
    get annotationOption() {
        if (isNil(this._annotationOptionApi)) {
            this._annotationOptionApi = new AnnotationOptionApi(this);
        }

        return this._annotationOptionApi;
    }
    
    get subject() {
        if (isNil(this._subjectApi)) {
            this._subjectApi = new SubjectApi(this);
        }

        return this._subjectApi;
    }

    get user() {
        if (isNil(this._userApi)) {
            this._userApi = new UserApi(this);
        }

        return this._userApi;
    }

    get mag() {
        if (isNil(this._magApi)) {
            this._magApi = new MagApi(this);
        }

        return this._magApi;
    }

    get contact() {
        if (isNil(this._contact)) {
            this._contact = new ContactApi(this);
        }

        return this._contact;
    }

    get(url, params) {
        return this.sendRequest(url, "GET", null, params);
    }

    post(url, document = null) {
        return this.sendRequest(url, "POST", document);
    }

    patch(url, document = null) {
        return this.sendRequest(url, "PATCH", document);
    }

    put(url, document = null) {
        return this.sendRequest(url, "PUT", document);
    }

    getHttpHeaders() {
        const headers = {
            "Content-Type": "application/hal+json",
        };

        if (this._accessToken !== null) {
            return {
                ...headers,
                Authorization: this._accessToken,
            };
        }

        return headers;
    }

    setAccessToken(accessToken) {
        this._accessToken = "JWT " + accessToken;
    }

    async sendRequest(path, method, document = null, params = null) {
        const config = {
            url: path,
            baseURL: this.apiUrl,
            method: method,
            headers: this.getHttpHeaders(),
        };

        if (document !== null) config.data = document;
        if (params !== null) config.params = params;

        return Axios.request(config)
            .catch(error => {
                if (error.response && error.response.data) {
                    return error.response;
                } else {
                    const { name, message, config } = error.toJSON();
                    throw new ApiError(name, { message, path: config.url, config });
                }
            })
            .then(response => {
                if (isNil(response.data) || !isNil(response.data.error)) {
                    const { error, ...payload } = response.data;
                    throw new ApiError(error, payload);
                } else {
                    return response.data;
                }
            });
    }
}

class ApiError {
    constructor(error, payload) {
        this.error = error;
        this.payload = payload;
    }
}
